import { Link } from "@tanstack/react-router";
import { Topbar } from "../Topbar";
import { Footer } from "../Footer";
import { Hero } from "../Hero";
import { CTABlock } from "../CTABlock";
import { ReadingProgress } from "./ReadingProgress";
import { BlogTOC } from "./BlogTOC";
import { BlogFAQ } from "./BlogFAQ";
import { LighthouseScoreDisplay } from "../LighthouseScoreDisplay";
import { formatDate, type BlogPost } from "@/lib/blog";
import { pathMap, SITE_URL } from "@/lib/locale";

type Props = { post: BlogPost };

export function BlogPostPage({ post }: Props) {
  const { fm, bodyHtml, headings } = post;
  const isEn = fm.locale === "en";
  const overviewPath = isEn ? pathMap.blog.en : pathMap.blog.de;
  const tocLabel = isEn ? "On this page" : "Inhaltsverzeichnis";
  const faqEyebrow = isEn ? "Frequently asked questions" : "Häufige Fragen";
  const faqHead = isEn ? "FAQ" : "Häufige Fragen";

  return (
    <>
      <ReadingProgress />
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow={fm.heroEyebrow}
          headline={
            fm.heroHeadlineEm ? (
              <>
                {fm.heroHeadline} <em>{fm.heroHeadlineEm}</em>
              </>
            ) : (
              fm.heroHeadline
            )
          }
          lead={fm.heroLead}
          metaStrip={fm.metaStrip}
          breadcrumbs={[
            { label: isEn ? "Start" : "Start", to: isEn ? "/en" : "/" },
            { label: "Blog", to: overviewPath },
            { label: fm.title.length > 50 ? fm.title.slice(0, 48) + "…" : fm.title },
          ]}
        />

        <section className="bg-white py-20">
          <div className="mx-auto max-w-[1200px] px-6 md:px-12">
            <div className="grid gap-10 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-16">
              <BlogTOC headings={headings} label={tocLabel} />
              <div className="min-w-0">
                {fm.slug === "lighthouse-99-performance-core-web-vitals" && (
                  <LighthouseScoreDisplay
                    locale={fm.locale}
                    scores={[
                      { label: isEn ? "Performance" : "Leistung", value: 99 },
                      { label: isEn ? "Accessibility" : "Barrierefreiheit", value: 100 },
                      { label: "Best Practices", value: 100 },
                      { label: "SEO", value: 100 },
                    ]}
                    vitals={[
                      { abbr: "FCP", label: "First Contentful Paint", value: isEn ? "0.7s" : "0,7s", color: "green" },
                      { abbr: "LCP", label: "Largest Contentful Paint", value: isEn ? "0.7s" : "0,7s", color: "green" },
                      { abbr: "TBT", label: "Total Blocking Time", value: "70ms", color: "green" },
                      { abbr: "CLS", label: "Cumulative Layout Shift", value: "0", color: "green" },
                    ]}
                    timestamp={
                      isEn
                        ? "calvarius.com · Lighthouse audit · 17 May 2026"
                        : "calvarius.com · Lighthouse-Audit · 17. Mai 2026"
                    }
                  />
                )}
                <article
                  className="blog-prose"
                  dangerouslySetInnerHTML={{ __html: bodyHtml }}
                />

                <BlogFAQ items={fm.faq} eyebrow={faqEyebrow} headline={faqHead} />

                <div className="mt-20">
                  <CTABlock
                    headline={
                      <>
                        <span className="block eyebrow mb-3 normal-case tracking-[0.18em]">
                          {fm.cta.eyebrow}
                        </span>
                        <em>{fm.cta.headline}</em>
                      </>
                    }
                    body={fm.cta.body}
                    primary={fm.cta.primary}
                    secondary={fm.cta.secondary}
                  />
                </div>

                <div className="mt-16 flex items-center justify-between border-t border-rule pt-6 font-mono text-xs uppercase tracking-wider text-ink-4">
                  <Link to={overviewPath} className="hover:text-cyan-deep">
                    ← {isEn ? "All posts" : "Alle Beiträge"}
                  </Link>
                  <span>
                    {isEn ? "Updated" : "Stand"}: {formatDate(fm.dateModified, fm.locale)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* JSON-LD: TechArticle / BlogPosting */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": fm.articleType ?? "BlogPosting",
            headline: fm.title,
            description: fm.description,
            image: `${SITE_URL}${fm.ogImage ?? "/og/default-1200x630.png"}`,
            url: `${SITE_URL}${isEn ? `/en/blog/${fm.slug}` : `/blog/${fm.slug}`}`,
            datePublished: fm.datePublished,
            dateModified: fm.dateModified,
            inLanguage: isEn ? "en-US" : "de-DE",
            articleSection: fm.category,
            keywords: fm.keywords.join(", "),
            wordCount: post.wordCount,
            author: {
              "@type": "Organization",
              name: "Calvarius GmbH",
              url: SITE_URL,
              foundingDate: "2013",
              knowsAbout: [
                "Performance Marketing",
                "SEO",
                "Conversion Rate Optimization",
                "Tracking Architecture",
              ],
            },
            publisher: {
              "@type": "Organization",
              name: "Calvarius GmbH",
              url: SITE_URL,
              logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.png` },
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `${SITE_URL}${isEn ? `/en/blog/${fm.slug}` : `/blog/${fm.slug}`}`,
            },
          }),
        }}
      />

      {/* JSON-LD: BreadcrumbList */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: isEn ? "Start" : "Start", item: `${SITE_URL}${isEn ? "/en" : "/"}` },
              { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}${overviewPath}` },
              { "@type": "ListItem", position: 3, name: fm.title, item: `${SITE_URL}${isEn ? `/en/blog/${fm.slug}` : `/blog/${fm.slug}`}` },
            ],
          }),
        }}
      />

      {/* JSON-LD: FAQPage */}
      {fm.faq.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: fm.faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          }}
        />
      )}
    </>
  );
}
